import React, { useState } from "react";

import Tippy from "@tippyjs/react";
import ModelVersionsAPI from "../../API/modelVersions.api";

import { longDate } from "../../utils/DateHelper";
import { MODEL_ACCESS, ROLES } from "../../utils/constants";
import ProductTags from "../Tags/ProductTags";
import { useTranslation } from "react-i18next";
import BiasCorrection from "../Modals/BiasCorrection";
import { isEmpty } from "lodash";
import InstructionNotification from "../Notifications/InstructionNotification";
import DeleteButton from "../Buttons/DeleteButton";
import EditButton from "../Buttons/EditButton";
import BaseActionButton from "../Buttons/BaseActionButton";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import BaseAlertModal from "../Modals/BaseAlertModal";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { useHistory } from "react-router-dom";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";

const VersionTable = ({
    headers,
    rows,
    handleDelete,
    handleActivate,
    accessType,
    editVersionCompeletion,
    showSelected = false,
    selectedVersion,
    setSelectedVersion,
    emptyMessage,
    modelParameterId,
    model,
    multiSelectVersion = false,
}) => {
    const [sortBy, setSortBy] = useState("createdAt");
    const [deleteButton, setDeleteButton] = useState(false);
    const [activateButton, setActivateButton] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(false);
    const [, setIsLoading] = useState(false);
    const [biasModal, setBiasModal] = useState(false);

    const history = useHistory();
    const { t } = useTranslation();

    const toggleSort = (name) => {
        if (sortBy === name) setSortBy(null);
        else setSortBy(name);
    };

    const handleDeleteClicked = (row, index) => {
        setDeleteButton(true);
        setSelectedIndex(index);
        setSelectedVersion(row);
        LogCapture.info(`version with id ${row?.id} is deleted`);
    };

    const handleActivateClicked = (row, index) => {
        setActivateButton(true);
        setSelectedIndex(index);
        setSelectedVersion(row);
    };

    const handleConfirmDelete = async () => {
        return handleDelete(selectedIndex);
    };

    const handleConfirmActivate = () => {
        return handleActivate(selectedIndex);
    };

    const handleGettingResultsFile = async (row) => {
        setIsLoading(true);
        const url = await ModelVersionsAPI.getVersionResultsFile(row?.id);
        LogCapture.info(`result files fetched of version with id ${row?.id}`);
        handleExternalLink(url);
    };

    const handleBiasCorrection = (row, index) => {
        setBiasModal(true);
        setSelectedIndex(index);
        setSelectedVersion(row);
        LogCapture.info(`bias correction of version with id ${row?.id}`);
    };

    const hasAccess = (type, authorize = false) => {
        if (authorize) {
            return isAuthorized(
                [ROLES.support, ROLES.partnerAdmin, ROLES.admin],
                getRoles()
            );
        }
        return type === accessType ? true : false;
    };

    const handleSelectRow = (row) => {
        setSelectedVersion(row);
    };

    const isRowSelected = (row) => {
        if (multiSelectVersion) {
            return selectedVersion.includes(row);
        } else {
            return selectedVersion?.id === row?.id;
        }
    };

    const renderHeadersData = () => {
        return (
            headers &&
            headers.map((header, index) => {
                if (!header?.role) {
                    return (
                        <th
                            key={index}
                            scope="col"
                            className={`px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider ${header.classNames}`}
                        >
                            <div className="flex items-center justify-center">
                                {!header.skipSort && (
                                    <i
                                        onClick={() => toggleSort(header.label)}
                                        className={`fas ${
                                            sortBy === header?.label
                                                ? "fa-sort-alpha-up-alt"
                                                : "fa-sort-alpha-down-alt"
                                        } table-head-icon`}
                                    />
                                )}
                                {header?.element || (
                                    <span className="ml-2">{header.label}</span>
                                )}
                            </div>
                        </th>
                    );
                }
            })
        );
    };

    const renderRowsData = () => {
        if (isEmpty(rows) && emptyMessage)
            return (
                <InstructionNotification
                    colSpan={headers.length}
                    message={emptyMessage}
                />
            );
        return (
            rows &&
            rows.map((row, index) => {
                return (
                    <tr
                        key={index}
                        className={`tr-ele  ${
                            showSelected && isRowSelected(row)
                                ? "steps-rows selected"
                                : ""
                        }`}
                        onClick={() => handleSelectRow(row)}
                    >
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.active ? (
                                <i className="fas fa-check-square success text-lg" />
                            ) : (
                                hasAccess(MODEL_ACCESS.owner) && (
                                    <i
                                        className="far fa-square text-lg cursor-pointer"
                                        onClick={() =>
                                            handleActivateClicked(row, index)
                                        }
                                    />
                                )
                            )}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.version}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap desc-width">
                            <div className="table-content">
                                <Tippy
                                    content={row?.description}
                                    placement="top"
                                    animation="scale"
                                >
                                    <span
                                        tabIndex="0"
                                        className="tooltip-container"
                                    >
                                        {row?.description}
                                    </span>
                                </Tippy>
                            </div>
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {longDate(row?.publishedAt)}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelType}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                            {`${row?.modelInfo?.minValue || 0} - ${
                                row?.modelInfo?.maxValue || 0
                            }`}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.R2Test || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.RMSETest || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.R2CV || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.RMSECV || "--"}
                        </td>

                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.BiasTest || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.RPDTest || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.BiasCV || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.RPDCV || "--"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row?.modelInfo?.numberOfSamples || "--"}
                        </td>
                        <td className=" whitespace-wrap p-2">
                            <ProductTags
                                tags={row?.supportedAccessories}
                                center={true}
                                emptyMessage={"---"}
                            />
                        </td>
                        <td className="px-6 py-3 whitespace-wrap flex sticky-column">
                            <EditButton
                                className={"mr-2"}
                                hide={!editVersionCompeletion}
                                onClick={() =>
                                    history.push({
                                        pathname: "/models/edit",
                                        state: {
                                            modelType: row?.modelType,
                                            data: {
                                                id: modelParameterId,
                                                modelId: model?.modelId,
                                                modelName: model?.modelName,
                                                materialName:
                                                    model?.materialName,
                                                createdAt: model?.createdAt,
                                                createdBy: model?.createdBy,
                                                parameterName:
                                                    model?.parameterSettings
                                                        ?.parameterName,
                                                decimalPlaces:
                                                    model?.parameterSettings
                                                        ?.decimalPlaces,
                                                deployed: model?.deployed,
                                                description:
                                                    model?.modelDescription,
                                            },
                                            modelParameterVersionId: row.id,
                                        },
                                    })
                                }
                                tippy={true}
                            />
                            <BaseActionButton
                                hide={!hasAccess(null, true)}
                                className="table-btn table-btn-blue mr-2"
                                onClick={() => handleBiasCorrection(row, index)}
                                icon={"fas fa-cog"}
                                tippySettings={{
                                    content: t("setBiasCorrection"),
                                }}
                            />
                            <DeleteButton
                                hide={!hasAccess(MODEL_ACCESS.owner)}
                                onClick={() => handleDeleteClicked(row, index)}
                                disabled={row?.active}
                                tippy={true}
                            />
                            <BaseActionButton
                                className="table-btn table-btn-blue"
                                disabled={!row?.resultsFilePath}
                                onClick={() => handleGettingResultsFile(row)}
                                icon={"fas fa-file-pdf"}
                                tippySettings={{
                                    content: t("downloadPdf"),
                                }}
                            />
                        </td>
                    </tr>
                );
            })
        );
    };

    return (
        <>
            <div className="flex flex-col mb-5">
                <div className="table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center">
                        <thead>
                            <tr>{renderHeadersData()}</tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {renderRowsData()}
                        </tbody>
                    </table>
                </div>
            </div>

            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("Delete version", {
                    version: selectedVersion?.version,
                })}
                message={t("deleteModelVersionMessage")}
                action={{ submitAction: handleConfirmDelete }}
                type="delete"
            />

            <BaseAlertModal
                isVisible={activateButton}
                setIsVisible={setActivateButton}
                title={t("activateVersionConfirmationTitle", {
                    version: selectedVersion?.version,
                })}
                message={t("activateVersionConfirmationMessage")}
                action={{
                    submitAction: handleConfirmActivate,
                }}
                type="confirmation"
            />
            <BiasCorrection
                modelId={selectedVersion?.id}
                isVisible={biasModal}
                setIsVisible={setBiasModal}
            />
        </>
    );
};

export default VersionTable;
